import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Parameter} from '../models/parameter';
import {Observable, of} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {api, handleError} from '../../shared/api-loaders';
import {ParameterKeys} from "../models/parameterKeys";
import {ThirdPartyIntegration} from '../models/ThirdPartyIntegration';
import { Select2Option } from '../../shared/select2-option';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ParameterService {

  addDefaultOption(options: any[]): any {
    options.unshift('Add New Vendor' );
    return options;
  }

  constructor(private http: HttpClient, private _http: HttpClient) { }

  getParameters(): Observable<Parameter[]> {
    return this._http
      .get(api.parameters()).pipe(
      catchError(handleError),);
  }

  getParametersByStack(): Observable<Parameter[]> {
    return this._http
      .get(api.parametersByStack()).pipe(
        catchError(handleError),);
  }

  getStackParameters(): Observable<Parameter[]> {
    return this._http
      .get(api.stackParameters()).pipe(
        catchError(handleError),);
  }

  getParametersByConfigId(configId: number): Observable<Parameter[]> {
    return this._http
      .get(api.parametersByConfigId(configId)).pipe(
        catchError(handleError),);
  }
                                                                                                                                                                                                                                                                                                                                                                 b
  getParameterByKey(configId: string, keyName: string): Observable<ParameterKeys[]> {
    return this._http
      .get(api.parameterByKey(configId, keyName)).pipe(
        catchError(handleError),);
  }

  getParametersByPartialKey(configId: string,partialKey: string, includeInherited: boolean): Observable<ParameterKeys[]> {
    return this._http
      .get(api.parametersByPartialKey(configId, partialKey,includeInherited)).pipe(
        catchError(handleError),);
  }

  getParameterByKeyValueOnly(configId: string, keyName: string): Observable<string> {
    return this._http
      .get(api.parameterByKeyValueOnly(configId, keyName),{ responseType: 'text'}).pipe(
          catchError(handleError),);
  }

  getApiHealthchecks(key: string, rootUrl: string): Observable<String[]> {
    return this._http
      .get(api.getApiHealthchecks(key,rootUrl)).pipe(
        catchError(handleError),);
  }

  getProductNames(): Observable<any> {

    //isDefault indicates if this product will be show in the Add All Parameters confirmation screen
    return of([
      { key: 'snf', name: 'Skilled Nursing', isDefault:true },
      { key: 'ecds', name: 'Enterprise Clinical Decision Support' },
      { key: 'sl', name: 'Senior Living', isDefault: true },
      { key: 'ca', name: 'CareAssist', isDefault: true},
      { key: 'mt', name: 'Meal Tracker', isDefault: true },
      { key: 'mcm', name: 'Marketing' },
      { key: 'cc', name: 'Care Community', isDefault: true },
      { key: 'intm', name: 'Matrixcare Marketing', isDefault: true },
      { key: 'bp', name: 'Platform Configurations', isDefault: true },
      { key: 'ng', name: 'NextGate' },
      { key: 'hhp', name: 'Home Health/Hospice', isDefault: true },
      { key: 'lpc', name: 'Life Plan Community', isDefault: true },
      { key: 'matrixcarecommunity', name: 'MatrixCare Community', isDefault: true },
      { key: 'servicediscovery', name: 'Service Discovery', isDefault: true },
      { key: 'ct', name: 'Matrixcare Connect', isDefault: true },
      { key: 'pd', name: 'Home Care Private Duty', isDefault: true },
      { key: 'hh', name: 'Home Health and Hospice (HHP)', isDefault: true },
      { key: 'provision', name: 'Provision', isDefault: true },
      { key: 'idmm', name: 'IDM Metrics', isDefault: true },
      { key: 'thirdpartyintegration', name: 'Third Party Vendors', isDefault: true },
      { key: 'ch', name: 'Citus Health (CH)', isDefault: true },
      { key: 'multicare', name: 'Multicare', isDefault: true },
      { key: 'paymentportal', name: 'Payment Portal', isDefault: true },
      { key: 'rt', name: 'Retail Tracker Online', isDefault: true },
      { key: 'sb', name: 'Solutions for Business', isDefault: true },
    ]);
  }

  /** GET */
  getParameter(id: number): Observable<Parameter> {
    return this._http
      .get(api.parameter(id)).pipe(
      catchError(handleError),);
  }

  /** PUT */
  updateParameter(id: number, parameter: Parameter): Observable<Parameter> {
    return this._http
      .put(api.parameter(id), parameter).pipe(
      catchError(handleError));
  }

  updateBatchParameter(id: number, parameter: Parameter[]): Observable<Parameter> {
    return this._http
      .put(api.parametersBulkUpdate(id), parameter).pipe(
        catchError(handleError));
  }

  /** POST */
  addParameter(parameter: Parameter): Observable<Parameter> {
    return this._http
      .post(api.parameters(), parameter).pipe(
      catchError(handleError),);
  }

  /** POST */
  addBatchParameter(parameter: Parameter[]): Observable<Parameter> {
    return this._http
      .post(api.addBatchParameters(), parameter).pipe(
        catchError(handleError),);
  }

  /** POST */
  addMyMatrixParameter(parameter: Parameter): Observable<string> {
    return this._http
      .post(api.addMyMatrixParameter(), parameter,{ responseType: 'text' as 'json' }).pipe(
        catchError(handleError),);
  }

  /** GET */
  getThirdPartyVendorParametersList(): Observable<any> {
    return this._http
      .get(api.thirdPartyVendorParameters()).pipe(
        catchError(handleError),);
  }

  /** POST */
  addThirdPartyParameters(thirdPartyVendor: ThirdPartyIntegration): Observable<any> {
    return this._http
      .post(api.thirdPartyVendorParameters(),thirdPartyVendor).pipe(
        catchError(handleError),);
  }

  /** DELETE */
  deleteParameter(id:number): Observable<Parameter> {
    return this._http
      .delete(api.parameter(id)).pipe(
      catchError(handleError));
  }

  deleteBatchParameter(parameters: string): Observable<Parameter> {
    return this._http
      .delete(api.parametersBulkDelete(parameters)).pipe(
        catchError(handleError));
  }

  deleteLauncherContext(id:string): Observable<any> {
    return this._http
      .delete(api.deleteLauncherContext(id)).pipe(
        catchError(handleError));
  }

  resetElasticDb(snfApiUrl : string,configId : string): Observable<string> {
    return this._http
      .put(api.resetElasticDb(snfApiUrl,configId), null, { responseType: 'text' as 'json' }).pipe(
        catchError(handleError));
  }

syncCisData(configId: string, username: string): Observable<string> {
  return this._http
    .post(api.syncCisData(configId, username), null, { responseType: 'text' as 'json' })
    .pipe(catchError(handleError));
}

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a message with the MessageService */
  private log(message: string) {
    console.log(`parameter.service: ${message}`);
  }

  getEnableDisableTypes() {
    return {
      True: 'True',
      False: 'False'
    }
  }

  getEnableDisableOptions(): Observable<Select2Option[]> {
    let types = this.getEnableDisableTypes();
    let options = Object.keys(types).map(x => {
      return { id: x, text: types[x] };
    });

    return of(options);
  }

  getEnabledDisableText(id: string) {
    const notFoundText = "False";
    if (!id)
      return notFoundText;
    return this.getEnableDisableTypes()[id] || notFoundText;
  }

  getGSDisplayTypes() {
    return {
      'First Name "Preferred Name" Last Name': 'First Name "Preferred Name" Last Name',
      'Last Name, First Name "Preferred Name"': 'Last Name, First Name "Preferred Name"'
    }
  }

  getGSDisplayOptions(): Observable<Select2Option[]> {
    let types = this.getGSDisplayTypes();
    let options = Object.keys(types).map(x => {
      return { id: x, text: types[x] };
    });

    return of(options);
  }

  getGSDisplayText(id: string) {
    const notFoundText = "First Name \"Preferred Name\" Last Name";

    if (!id)
      return notFoundText;
    return this.getGSDisplayTypes()[id] || notFoundText;
  }

  /** PUT */
  hotReload(parameter: Parameter): Observable<Parameter> {
    return this._http
      .put(api.hotReload(), parameter).pipe(
        catchError(handleError));
  }

  /** PUT */
  stackHotReload(parameter: Parameter): Observable<Parameter> {
    return this._http
      .put(api.stackHotReload(), parameter).pipe(
        catchError(handleError));
  }
}
